import './AcknowledmentFormComponent.scss';
import React, {useCallback, useEffect, useRef, useState} from "react";
import {Field, FieldProps, Form, Formik, FormikHelpers, FormikProps} from "formik";
import FormikCheckBoxComponent from "../../../shared/components/form-controls/formik-check-box/FormikCheckBoxComponent";
import FormikInputComponent from "../../../shared/components/form-controls/formik-input/FormikInputComponent";
import FormikDatePickerComponent
    from "../../../shared/components/form-controls/formik-date-picker/FormikDatePickerComponent";
import FormControlLabelComponent from "../../../shared/components/form-control-label/FormControlLabelComponent";
import ButtonComponent from "../../../shared/components/button/ButtonComponent";
import _ from "lodash";
import ErrorComponent from "../../../shared/components/error/ErrorComponent";
import ESignApprovalComponent from "../../../shared/components/e-sign-approval/ESignApprovalComponent";
import LinkComponent from "../../../shared/components/link/LinkComponent";
import * as Yup from "yup";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import moment from "moment";
import ModalComponent from "../../../shared/components/modal/ModalComponent";
import SignaturePopupComponent from "../../../shared/components/signature-popup/SignaturePopupComponent";
import momentTimezone from "moment-timezone";

interface AcknowledgementFormComponentProps {
    onSubmit: any;
    setIsAcknowledgementModalOpen: any;
    isLoading?: boolean;
}

const initialValues = {
    acknowledgement_details: {
        client_name: '',
        is_terms_acknowledged: false,
        is_authorized_representative: false,
        representative_name: '',
        signature: '',
        date: '',
        timezone: "",
    },
};

const acknowledgementValidationSchema = Yup.object({
    acknowledgement_details: Yup.object({
        client_name: Yup.string().required('Name is required'),
        date: Yup.string().nullable(),
        is_terms_acknowledged: Yup.boolean().required(),
        is_authorized_representative: Yup.boolean(),
        representative_name: Yup.string().when("is_authorized_representative", {
            is: true,
            then: Yup.string().required('Authorized Representative Name is required'),
            otherwise: Yup.string().nullable(),
        }),
        signature: Yup.string().required('Signature is required'),
    }),
});

const AcknowledgementFormComponent = (props: AcknowledgementFormComponentProps) => {
    const {onSubmit, setIsAcknowledgementModalOpen, isLoading} = props;
    const [acknowledgementFormInitialValues, setAcknowledgementFormInitialValues] = useState(_.cloneDeep(initialValues));
    const formikRef = useRef<FormikProps<typeof initialValues>>(null);
    const [isSignaturePopupOpen, setIsSignaturePopupOpen] = useState<boolean>(false);
    const {
        clientBasicDetails,

    } = useSelector((state: IRootReducerState) => state.client);
    const handleSubmit = useCallback((values: any, {setErrors, setSubmitting}: FormikHelpers<any>) => {
        const {acknowledgement_details} = values;
        // Exclude certain fields if needed
        const payload = {
            acknowledgement_details: {
                client_name: acknowledgement_details.client_name,
                is_terms_acknowledged: acknowledgement_details.is_terms_acknowledged,
                is_authorized_representative: acknowledgement_details.is_authorized_representative || false,
                representative_name: acknowledgement_details.representative_name,
                signature: acknowledgement_details.signature,
                date: acknowledgement_details.date,
                timezone: acknowledgement_details.timezone
            }
        };
        onSubmit(payload);
        setSubmitting(false);
    }, [onSubmit]);

    useEffect(() => {
        setAcknowledgementFormInitialValues((prevState: any) => {
            return {
                ...prevState,
                acknowledgement_details: {
                    client_name: clientBasicDetails?.first_name + " " + clientBasicDetails?.last_name,
                    date: moment().format('YYYY-MM-DD'),
                    timezone: momentTimezone.tz.guess()
                }
            }
        })
    }, [clientBasicDetails]);

    const handleSignaturePopupClose = useCallback(() => {
        setIsSignaturePopupOpen(false);
    }, []);


    return (
        <div className={'acknowledgement-form'}>
            <div className={'acknowledgement-form__header'}>Acknowledgment of Updated Information</div>
            <div className={'acknowledgement-form__body'}>
                I hereby certify that the information that I have just updated on this form is true, accurate, current,
                and complete.<br/><br/>
                I understand that my medical history is a critical factor in determining the direction of my care. I
                recognize that failing to disclose any known medical <br/>or physical conditions may result in serious
                injury. If any of the information I have provided changes, I will promptly inform Kinergy Sports
                Medicine <br/> and Performance or my provider. I knowingly and willingly assume all risks of injury
                resulting
                from my failure to provide accurate, complete, and<br/> updated information as required by this
                questionnaire.<br/><br/>
                I hereby consent to and authorize Kinergy Sports Medicine and Performance to perform any assessments and
                treatments deemed necessary for my<br/> care. I acknowledge that participating in activities and
                receiving
                care at Kinergy Sports Medicine and Performance may involve certain risks.<br/> I understand that I have
                the
                right to inquire about these risks and to have any questions answered prior to proceeding with any
                services or activities.<br/> I also understand that I may revoke this consent at any time, except
                retrospectively. Furthermore, I acknowledge that no guarantees or assurances<br/> have been made
                regarding
                the outcome of the care I will receive.
            </div>
            <div>
                <Formik
                    validationSchema={acknowledgementValidationSchema}
                    initialValues={acknowledgementFormInitialValues}
                    validateOnChange={false}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    innerRef={formikRef}
                    validateOnMount={true}
                    onSubmit={handleSubmit}
                >
                    {({values, validateForm, touched, setSubmitting, errors, isValid, isSubmitting, setFieldValue}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);
                        return (
                            <Form className="t-form" noValidate={true}>
                                <div className="t-form-controls">
                                    <div className="ts-row pdd-top-20">
                                        <div className="ts-col-12">
                                            <Field name={'acknowledgement_details.is_terms_acknowledged'}
                                                   className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikCheckBoxComponent
                                                            label={"I acknowledge that I have read and agree to the statement as written above."}
                                                            required={true}
                                                            formikField={field}
                                                            color={'accent-color'}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-12">
                                            <Field name={'acknowledgement_details.is_authorized_representative'}
                                                   className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikCheckBoxComponent
                                                            label={'I am an Authorized Representative for the client (ie. Parent, Legal Guardian, Agent, Manager, etc.)'}
                                                            formikField={field}
                                                            color={'accent-color'}
                                                            onChange={() => {
                                                                setFieldValue('acknowledgement_details.representative_name', '');
                                                            }}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-lg-6 ts-col-md-12 ">
                                            <Field name={'acknowledgement_details.client_name'}
                                                   className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            id={"name_input"}
                                                            label={"Client Name"}
                                                            placeholder={"Enter Client's Name"}
                                                            disabled={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                            titleCase={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className="ts-col-lg-6 ts-col-md-12 ">
                                            <Field name={'acknowledgement_details.date'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikDatePickerComponent
                                                            id={"dob_input"}
                                                            label={'Date'}
                                                            placeholder={'Enter Date'}
                                                            formikField={field}
                                                            fullWidth={true}
                                                            disabled={true}
                                                            readOnly={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    {
                                        values.acknowledgement_details?.is_authorized_representative &&
                                        <div className="ts-row">
                                            <div className="ts-col-lg-6 ts-col-md-12 ">
                                                <Field name={'acknowledgement_details.representative_name'}
                                                       className="t-form-control">
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Authorized Representative Name'}
                                                                placeholder={'Enter Representative Name'}
                                                                required={true}
                                                                fullWidth={true}
                                                                formikField={field}
                                                                titleCase={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                        </div>
                                    }

                                    {
                                        !values?.acknowledgement_details?.signature &&
                                        <div className="ts-row">
                                            <div className="ts-col-12">
                                                <FormControlLabelComponent
                                                    className={"font-weight-thin signature-label"}
                                                    label={"Signature of Client or Authorized Representative"}
                                                    required={true}/>
                                                <ButtonComponent
                                                    onClick={() => {
                                                        setIsSignaturePopupOpen(true);
                                                    }}
                                                    className={'add-signature-button'}
                                                    color={'accent'}
                                                    variant={'outlined'}
                                                    size={'medium'}
                                                >
                                                    Add Signature
                                                </ButtonComponent>
                                            </div>
                                            {
                                                (_.get(touched, "acknowledgement_details.signature") && !!(_.get(errors, "acknowledgement_details.signature"))) &&
                                                <ErrorComponent
                                                    errorText={(_.get(errors, "acknowledgement_details.signature"))}/>
                                            }
                                        </div>
                                    }
                                    {values?.acknowledgement_details?.signature &&
                                        <div className="ts-row">
                                            <div className={'ts-col-6 mrg-0'}>
                                                <ESignApprovalComponent isSigned={true}
                                                                        signature_url={values?.acknowledgement_details?.signature}
                                                />
                                                <div className={'signature-remove-button'}>
                                                    <LinkComponent onClick={() => {
                                                        setFieldValue('acknowledgement_details.signature', "");
                                                    }
                                                    }>
                                                        Remove Signature
                                                    </LinkComponent>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    <div className="t-form-actions">
                                        <ButtonComponent
                                            variant={'outlined'}
                                            color={'accent'}
                                            onClick={() => {
                                                setIsAcknowledgementModalOpen(false)
                                                setSubmitting(false)
                                            }}
                                        >
                                            Cancel
                                        </ButtonComponent>
                                        <ButtonComponent
                                            id={"submit_btn"}
                                            type={"submit"}
                                            className={'mrg-left-20'}
                                            color={'accent'}
                                            disabled={!isValid || isLoading}
                                            isLoading={isSubmitting || isLoading}
                                        >
                                            Save
                                        </ButtonComponent>
                                    </div>
                                </div>
                            </Form>
                        )
                    }}
                </Formik>
                <ModalComponent isOpen={isSignaturePopupOpen}
                                onClose={handleSignaturePopupClose}
                                size={"xl"}
                >
                    <SignaturePopupComponent
                        onSign={(signImage: any) => {
                            if (formikRef?.current) {
                                formikRef.current.setFieldValue(
                                    'acknowledgement_details.signature',
                                    signImage
                                );
                            }

                            handleSignaturePopupClose();
                        }}
                        handleSignaturePopupClose={handleSignaturePopupClose}
                    />
                </ModalComponent>
            </div>
        </div>
    )
}

export default AcknowledgementFormComponent;
